import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Message, Checkbox, Icon } from 'react-frontier';
import { useValidator } from '@arema/components/Hooks';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthHooks';
import { SetLoading } from '@arema/components/Classes';
import { bindClose } from '@arema/components/Util';
import { useTranslation } from 'react-i18next';
import API, { CDN_URL } from '../AuthAPI';
import LoginSaved from './LoginSaved';
import UserProfile from './UserProfile';
import '../scss/login.scss';

var Divider = (props: { text?: string, style?: React.CSSProperties })=>{
	if(!props.text){
		return <div className="divider" style={props.style}>
			<div className="line"></div>
		</div>
	}
	return <div className='divider' style={props.style}>
		<div className="line"></div>
		<div className="text">{props.text}</div>
		<div className="line"></div>
	</div>
}

var Login = ()=>{
	var { t } = useTranslation();
	var { is_pdv, user, user_saved, params, getContinueUrl, loggedIn, getCaptcha } = useAuth();
	var { search } = useLocation();
	var LoginForm = useValidator({
		email: '',
		password: '',
		code: '',
		remember: false,
	}, {
		email: [{ rule: 'email', prompt: t('login.prompts.email') }],
		password: [{ rule: 'minLength', params: [6], prompt: t('login.prompts.password', { amount: 6 }) }],
	});

	var sendLogin = async (setLoading: SetLoading)=>{
		var { valid } = LoginForm.validate(null, is_pdv && LoginForm.data.remember ? {
			code: [{ rule: 'length', params: [4], prompt: t('login.prompts.code') }]
		} : null);
		if(!valid) return;

		setLoading(true);
		try{
			var captcha = await getCaptcha();
			if(!captcha){
				setLoading(false);
				return LoginForm.setPrompts([t('common.captcha_error')]);
			}
		}catch(e){
			LoginForm.setPrompts([t('common.captcha_error')]);
			setLoading(false);
			return;
		}

		var remember = !is_pdv || LoginForm.data.remember;
		try{
			var res = await API.login(LoginForm.data.email, LoginForm.data.password, params.source, captcha, remember, (remember && is_pdv) ? LoginForm.data.code : null, params.pdv_token, !params.continue_url);
			if(res.error) return LoginForm.setPrompts([res.message]);
			if(params.continue_url){
				setTimeout(()=>{
					var url = getContinueUrl(res.data.tokens);
					if(!url) return;
					window.location.href = url;
				}, 1000);
			}
		}catch(e){
			return LoginForm.setPrompts([t('common.unexpected_code', { code: 'LCL-ATHLG-1' })]);
		}finally{
			setLoading(false);
		}
	}

	if(user && loggedIn && !params.continue_url){
		return <UserProfile />
	}

	return <div className="ar login-screen">
		<div className="contents">
			<img className='ar logo' src={`${CDN_URL}/logo/LogoH_Large.webp`} alt="AREMA" />

			{(user_saved && !loggedIn) ? (
				<LoginSaved />
			) : (!!user && loggedIn) ? (
				<div className="complete">
					<Icon name='check-circle' color='green' />
					<div className="title">{t('login.hello', { name: user.user.first_name })}</div>
					<div className="subtitle">{t('login.redirecting')}</div>
					<Button color='black' text={t('common.continue')} as={Link} to={getContinueUrl()} />
				</div>
			) : <>
				<div className="title">
					{t('login.title')}
				</div>
				<div className="subtitle">
					{t('login.subtitle')}
				</div>
				<div className="form">
					<Input placeholder={t('common.email')} value={LoginForm.data.email} onChange={LoginForm.onDataChange('email')} error={LoginForm.errors.email} />
					<Input placeholder={t('common.password')} value={LoginForm.data.password} onChange={LoginForm.onDataChange('password')} inputType='password' error={LoginForm.errors.password} submitOnEnter />
					{is_pdv && (
						<Checkbox label={t('login.remember')} color='orange' style={{ marginBottom: 10, marginTop: 10 }} checked={LoginForm.data.remember} onChange={LoginForm.onDataChange('remember')} />
					)}
					{!!is_pdv && !!LoginForm.data.remember && (
						<Input label={t('login.code_label')} comment={t('login.code_description')} value={LoginForm.data.code} onChange={LoginForm.onDataChange('code')} error={LoginForm.errors.code} labelStyle={{ textAlign: 'center' }} commentStyle={{ textAlign: 'center' }} style={{ marginTop: 20 }} inputStyle={{ textAlign: 'center', fontSize: 20 }} inputType='password' maxLength={4} />
					)}
					<Message type='error' list={LoginForm.prompts} style={{ marginTop: 10, marginBottom: 10 }} />
					<Button text={t('common.login')} color='orange' fluid onClick={sendLogin}  />
					
					{!is_pdv && (
						<Divider text={t('login.other_options')} style={{ marginBottom: 20, marginTop: 15 }} />
					)}
					<div className="others">
						{/* <Button text={t('login.auth.passkey')} iconName='key' color='blue' /> */}
						{/* <Button text={t('login.auth.apple')} iconName='apple' color='black' className='brand' /> */}
						{/* <Button text={t('login.auth.google')} iconName='google' color='blue' className='brand' /> */}
						{!is_pdv && (
							<Button text={t('login.create_account')} iconName='plus' className='gray' as={Link} to={`/create${search}`} />
						)}
					</div>
					{!is_pdv && <>
						<Divider style={{ marginTop: 25, marginBottom: 25 }} />
						<div className="create">
							¿No tienes una cuenta?
							<Button text='Crear cuenta' size='tiny' basic color='black' as={Link} to={`/create${search}`} />
						</div>
						<div className="create">
							<Button text='¿Olvidaste tu contraseña?' size='tiny' basic color='black' as={Link} to={`/forgot${search}`} />
						</div>
					</>}
				</div>
			</>}
		</div>
	</div>
}

// enum LoginMode{
// 	LOGIN = 1,
// 	FORGOT = 2,
// 	TOTP = 3,
// 	CHANGE = 4,
// 	RECOVER = 5,
// 	CREATE = 6,
// 	ACCOUNT_CREATED = 7,
// }

// interface LoginFormData{
// 	email: string,
// 	forgot_email: string,
// 	password: string,
// 	totp: string,
// 	code: string,
// 	captcha: string,
// 	remember: boolean,
// }

// interface PasswordForm{
// 	user?: {
// 		user_id: number,
// 		admin: boolean,
// 		email: string,
// 	},
// 	token?: string,
// 	new_password: string,
// 	repeat_password: string,
// }

// interface ChangePasswordForm extends PasswordForm{
// 	current_password: string,
// }

// export interface CreateFormData{
// 	first_name: string,
// 	last_name: string,
// 	email: string,
// 	password: string,
// 	captcha?: string,
// 	repeat_password: string,
// }

// var ChangePasswordMessage = (props: { email: string, admin?: boolean, expired?: boolean })=>{
// 	return (
// 		<Message style={{ marginBottom: 15 }} type="info" text={`${props.expired ? 'La contraseña de AREMA de esta cuenta ha sido marcada como expirada.' : ''} Favor de ingresar una nueva contraseña`} list={[
// 			`Usuario: ${props.email}`,
// 			props.admin ? (
// 				'Por seguridad, la contraseña debe de ser de mínimo 8 caracteres y debe de tener por lo menos: 1 número, 1 letra mayúscula, 1 letra minúscula y 1 caracter especial (!@#$%^&*.,+).'
// 			) : (
// 				'La contraseña debe de ser de mínimo 8 caracteres'
// 			)
// 		]} />
// 	)
// }

// var Login = ()=>{
// 	var recaptchaRef = useRef<ReCAPTCHA>(null);
// 	var { user, user_saved, clearSaved, loginSaved } = useAuth();
// 	var [query, setQuery] = useSearchParams();
// 	var [mode, setMode] = useState<LoginMode>(LoginMode.LOGIN);
// 	var [sending, setSending] = useState<boolean>(false);
// 	var [loginTokens, setLoginTokens] = useState<AuthTokens>(null);
// 	var [loginError, setLoginError] = useState<string>(null);
// 	var [shownError, setShownError] = useState<string>(null);
// 	var [shownMessage, setShownMessage] = useState<{ icon: IconName, title: string, substitle?: string }>(null);
// 	var [forgotSent, setForgotSent] = useState<boolean>(false);
// 	var [successMessage, setSuccessMessage] = useState<string>(null);
// 	var ChangeForm = useValidator<ChangePasswordForm>({
// 		current_password: '',
// 		new_password: '',
// 		repeat_password: '',
// 	}, {
// 		current_password: [{ rule: 'minLength', params: [5] }],
// 		new_password: [{ rule: DEBUG ? 'empty' : 'password' }],
// 		repeat_password: [{ rule: 'matches', params: ['new_password'], prompt: 'Las contraseñas no concuerdan' }],
// 	});
// 	var RecoverForm = useValidator<PasswordForm>({
// 		new_password: '',
// 		repeat_password: '',
// 	}, {
// 		new_password: [{ rule: DEBUG ? 'empty' : 'password' }],
// 		repeat_password: [{ rule: 'matches', params: ['new_password'], prompt: 'Las contraseñas no concuerdan' }],
// 	});
//  	var LoginForm = useValidator<LoginFormData>({
// 		email: '',
// 		forgot_email: '',
// 		password: '',
// 		totp: '',
// 		code: '',
// 		captcha: null,
// 		remember: false,
// 	}, {
// 		email: [{
// 			rule: 'email',
// 			params: [5],
// 			prompt: `El correo electrónico no es válido`
// 		}],
// 	});
// 	var CreateForm = useValidator<CreateFormData>({
// 		first_name: '',
// 		last_name: '',
// 		email: '',
// 		password: '',
// 		repeat_password: '',
// 		captcha: '',
// 	}, {
// 		first_name: [{ rule: 'length', params: [3, 32], label: 'Nombre' }],
// 		last_name: [{ rule: 'length', params: [3, 64], label: 'Apellido' }],
// 		email: [{ rule: 'email', label: 'Correo electrónico' }],
// 		password: [{ rule: 'length', params: [8, 128], label: 'Contraseña' }],
// 		repeat_password: [{ rule: 'matches', params: ['password'], prompt: 'Las contraseñas no concuerdan.' }],
// 	})

// 	var login_source : LoginSource = parseInt(query.get('s'));
// 	var pdv_token = query.get('pdv');
// 	var recover_token = query.get('tkn');
// 	var confirm_token = query.get('ct');
// 	var is_pdv = pdv_token && pdv_token.length>10;
// 	var show_saved = !is_pdv && query.get('sv')!=='0';
// 	var auth_url = decodeURIComponent(query.get('continue') || '');

// 	var valid_source = Object.values(LoginSource).indexOf(login_source)!=-1
// 	var valid_continue = !!auth_url && auth_url.length>=10;

// 	useEffect(()=>{
// 		var recover_token = query.get('tkn');
// 		if(recover_token && recover_token.length>10){
// 			setMode(LoginMode.RECOVER)
// 			isRecoverTokenValid()
// 		}else if(confirm_token){

// 		}
// 	}, []);

// 	useEffect(()=>{
// 		recaptchaRef.current?.reset();
// 		setLoginError(null);
// 	}, [mode]);

// 	var redirect_url = useMemo(()=>query.get('redirect'), [query]);
// 	var getContinueUrl = (tokens: AuthTokens)=>{
// 		if(!auth_url || !tokens) return null;
// 		auth_url = auth_url.replace(/\/$/gi, '');
// 		var params : any = {
// 			at: tokens.access,
// 			e: tokens.expires,
// 		}
// 		if(tokens.refresh && [LoginSource.PDV, LoginSource.SCANNER].indexOf(login_source)!=-1){
// 			params.rt = tokens.refresh;
// 			params.re = tokens.refresh_expires;
// 		}
		
// 		if(redirect_url && redirect_url.length>=10){
// 			params.r = redirect_url;
// 		}
// 		return `${auth_url}?${Object.keys(params).map(a=>`${a}=${params[a]}`).join('&')}`
// 	}
	
// 	var isRecoverTokenValid = ()=>{
// 		API.verifyToken(recover_token).then(res=>{
// 			if(res.error){
// 				setQuery(q=>{
// 					q.delete('tkn');
// 					return q;
// 				});
// 				setMode(LoginMode.LOGIN);
// 				return setShownError(res.message);
// 			}
// 			RecoverForm.setData({
// 				user: {
// 					user_id: res.data.user_id,
// 					admin: res.data.admin,
// 					email: res.data.email,
// 				},
// 				token: recover_token,
// 				new_password: '',
// 				repeat_password: '',
// 			});
// 			setMode(LoginMode.RECOVER);
// 		}).catch(()=>{
// 			setMode(LoginMode.LOGIN);
// 			setShownError('El token es invalido o se vencío (LCL-RCV-2)');
// 		})
// 	}

// 	var getButtonText = ()=>{
// 		switch(mode){
// 			case LoginMode.LOGIN: return 'Iniciar sesión';
// 			case LoginMode.CREATE: return 'Crear cuenta';
// 			default: return 'Continuar';
// 		}
// 	}

// 	var getHeaderText = ()=>{
// 		switch(mode){
// 			case LoginMode.CREATE:
// 			case LoginMode.ACCOUNT_CREATED: return 'Crear cuenta';
// 			default: return 'Iniciar sesión';
// 		}
// 	}

// 	var resetPrompts = ()=>{
// 		LoginForm.setPrompts(null);
// 		ChangeForm.setPrompts(null);
// 		RecoverForm.setPrompts(null);
// 		CreateForm.setPrompts(null);
// 		setSuccessMessage(null);
// 	}

// 	var refreshAuth = ()=>{
// 		if(!user_saved) return;
// 		setSending(true);
// 		loginSaved(login_source).then(res=>{
// 			if(res.error) return setLoginError(res.message);
// 			if(!res.data.tokens?.access){
// 				return setLoginError(`Hubo un error inesperado iniciando sesión (LCL-SVD-2)`);
// 			}
// 			setLoginTokens(res.data.tokens);
// 			setTimeout(()=>{
// 				window.location.href = getContinueUrl(res.data.tokens);
// 			}, 1000);
// 		}).catch(err=>{
// 			setLoginError(`Hubo un error inesperado haciendo login. (LCL-SVD-1)`);
// 		}).finally(()=>{
// 			setSending(false);
// 		})
// 	}

// 	var submitLogin = (captcha: string)=>{
// 		if(Number.isNaN(login_source)){
// 			login_source = LoginSource.GENERIC
// 		}
		
// 		setSending(true);
// 		var { data } = LoginForm
// 		API.login(data.email, data.password, login_source, captcha, data.remember, data.remember ? (data.code || null) : null, is_pdv ? pdv_token : null).then(res=>{
// 			if(res.error) return LoginForm.setPrompts([res.message]);
// 			if(res.data.change_password){
// 				ChangeForm.setData({
// 					user: {
// 						user_id: res.data.user.user_id,
// 						admin: res.data.change_password.admin,
// 						email: res.data.user.email,
// 					},
// 					token: res.data.change_password.token,
// 					current_password: '',
// 					new_password: '',
// 					repeat_password: '',
// 				});
// 				LoginForm.reset();
// 				return setMode(LoginMode.CHANGE);
// 			}
// 			if(!res.data.tokens?.access){
// 				return LoginForm.setPrompts(['Hubo un error inesperado iniciando sesión (LCL-ADMLG-2)']);
// 			}
// 			setLoginTokens(res.data.tokens);
// 			setTimeout(()=>{
// 				window.location.href = getContinueUrl(res.data.tokens);
// 			}, 1000);
// 		}).catch(err=>{
// 			LoginForm.setPrompts(['Hubo un error inesperado iniciando sesión (LCL-ADMLG-1)']);
// 		}).finally(()=>{
// 			setSending(false);
// 		});
// 	}

// 	var submitCreate = (captcha: string)=>{
// 		var { valid, errors, prompts } = CreateForm.validate({ captcha });
// 		if(!valid) return;

// 		CreateForm.setPrompts(null);
// 		setSending(true);
// 		API.createAccount({
// 			first_name: CreateForm.data.first_name,
// 			last_name: CreateForm.data.last_name,
// 			email: CreateForm.data.email,
// 			password: CreateForm.data.password,
// 			captcha,
// 		}).then(res=>{
// 			if(res.error) return CreateForm.setPrompts([res.message]);
// 			setMode(LoginMode.ACCOUNT_CREATED);
// 		}).catch(err=>{
// 			CreateForm.setPrompts(['Hubo un error inesperado creando la cuenta (LCL-ATHCRA-1)']);
// 		}).finally(()=>{
// 			setSending(false);
// 		})
// 	}

// 	var submitForgot = (captcha: string)=>{
// 		var { valid } = LoginForm.validate({ captcha }, {
// 			email: [{
// 				rule: 'email', prompt: 'El correo electrónico no es válido',
// 			}]
// 		});
// 		if(!valid) return;
// 		LoginForm.setPrompts(null);
// 		setSending(true);
// 		API.sendForgot(LoginForm.data.email, login_source, captcha).then(res=>{
// 			if(res.error) return LoginForm.setPrompts([res.message]);
// 			setMode(LoginMode.LOGIN)
// 			setForgotSent(true);
// 		}).catch(err=>{
// 			LoginForm.setPrompts(['Hubo un error haciendo la solicitud de contraseña. (LCL-ATHFP-1)']);
// 		}).finally(()=>{
// 			setSending(false);
// 		});
// 	}

// 	var submitRecover = ()=>{
// 		var { valid } = RecoverForm.validate(null, {
// 			new_password: [{ 
// 				label: 'Nueva contraseña',
// 				rule: DEBUG ? 'length' : (RecoverForm.data.user.admin ? 'password' : 'length'),
// 				params: [8, 127], 
// 			}],
// 		})
// 		if(!valid) return;

// 		setSending(true);
// 		RecoverForm.setPrompts(null);
// 		API.recoverPassword(recover_token, RecoverForm.data.new_password).then(res=>{
// 			if(res.error) return RecoverForm.setPrompts([res.message]);
// 			resetPrompts();
// 			RecoverForm.reset();
// 			setQuery(q=>{
// 				q.delete('tkn');
// 				return q;
// 			});
// 			if(valid_continue && valid_source){
// 				setMode(LoginMode.LOGIN);
// 				setSuccessMessage('Se ha cambiado la contraseña, favor de iniciar sesión de nuevo.');
// 			}else{
// 				setShownMessage({
// 					icon: 'check-circle',
// 					title: 'Contraseña actualizada',
// 					substitle: 'Se ha cambiado la contraseña de la cuenta'
// 				});
// 			}
// 		}).catch(err=>{
// 			RecoverForm.setPrompts(['Hubo un error inesperado reestableciendo la contraseña (LCL-ATHRCV-1)']);
// 		}).finally(()=>{
// 			setSending(false);
// 		});
// 	}
	
// 	var submitChange = ()=>{
// 		var { valid } = ChangeForm.validate(null, {
// 			new_password: [{ rule: ChangeForm.data.user.admin ? 'password' : 'length', params: [8, 127], label: 'Nueva contraseña' }],
// 		});
// 		if(!valid) return;

// 		setSending(true);
// 		LoginForm.setPrompts(null);
// 		API.changePassword(ChangeForm.data.token, ChangeForm.data.current_password, ChangeForm.data.new_password).then(res=>{
// 			if(res.error) return ChangeForm.setPrompts([res.message]);
// 			resetPrompts();
// 			setMode(LoginMode.LOGIN);
// 			setSuccessMessage('Se ha cambiado la contraseña, favor de iniciar sesión de nuevo.');
// 		}).catch(err=>{
// 			ChangeForm.setPrompts(['Hubo un error inesperado cambiando la contraseña (LCL-ATHCHP-1)']);
// 		}).finally(()=>{
// 			setSending(false);
// 		});
// 	}

// 	var submit = async ()=>{
// 		if(mode===LoginMode.CHANGE) return submitChange();
// 		if(mode===LoginMode.RECOVER) return submitRecover();

// 		if(mode===LoginMode.LOGIN){
// 			var { valid } = LoginForm.validate(null, {
// 				password: [{
// 					rule: 'minLength', params: [6], prompt: 'La contraseña debe de ser de mínimo 6 caracteres', if: mode===LoginMode.LOGIN
// 				}],
// 				code: [{
// 					rule: /[0-9]{4}/gi, prompt: 'El código debe de ser un numerico de 4 digitos', if: (is_pdv && LoginForm.data.remember)
// 				}]
// 			});
// 			if(!valid) return;
// 		}

// 		var CurrentForm = mode===LoginMode.CREATE ? CreateForm : LoginForm;
// 		var { valid } = CurrentForm.validate();
// 		if(!valid) return;

// 		var cph : string = null;
// 		if(CAPTCHA_ACTIVE){
// 			try{
// 				setSending(true);
// 				recaptchaRef.current.reset();
// 				var cph = await recaptchaRef.current.executeAsync()
// 				if(!cph) return CurrentForm.setPrompts(['Hubo un error realizando el captcha (LCL-ATHSB-1)']);
// 				CurrentForm.onDataChange('captcha')(cph);
// 			}catch(e){
// 				return CurrentForm.setPrompts(['Hubo un error realizando el captcha (LCL-ATHSB-2)']);
// 			}finally{
// 				setSending(false);
// 			}
// 		}
		
// 		switch(mode){
// 			case LoginMode.LOGIN: return submitLogin(cph);
// 			case LoginMode.FORGOT: return submitForgot(cph);
// 			case LoginMode.CREATE: return submitCreate(cph);
// 		}
// 	}

// 	var returnMode = ()=>{
// 		LoginForm.reset();
// 		ChangeForm.reset();
// 		RecoverForm.reset();
// 		CreateForm.reset();
// 		resetPrompts();
// 		setMode(LoginMode.LOGIN);
// 	}

// 	var actions_shown = mode!==LoginMode.LOGIN && mode!==LoginMode.ACCOUNT_CREATED && (mode!=LoginMode.RECOVER || RecoverForm?.data.user)
	
// 	return <div></div>
// 	// return <div className={classNames("ar login-screen", {
// 	// 	change: mode===LoginMode.CHANGE
// 	// })}>
// 	// 	<img className='ar logo' src={`${CDN_URL}/logo/LogoH_Large.webp`} alt="AREMA" />
// 	// 	<Groupper defaultStyle={false} actions={!!actions_shown && <>
// 	// 		<Button basic text={'Regresar'} onClick={returnMode} style={{ marginRight: 5 }} />
// 	// 		<Button color='black' text={getButtonText()} onClick={submit} loading={sending} />
// 	// 	</>}>
// 	// 		{shownError ? (
// 	// 			<div className="contents semi full">
// 	// 				<Header iconName='exclamation-triangle' iconStyle={{ fontSize: 70, marginBottom: 15 }} text='Error' subtext={shownError} />
// 	// 				<Button text='Regresar' style={{ marginTop: 15, minWidth: 200 }} onClick={()=>setShownError(null)} loading={false} />
// 	// 			</div>
// 	// 		) : shownMessage ? (
// 	// 			<div className="contents full">
// 	// 				<Header iconName={shownMessage.icon} iconStyle={{ fontSize: 70, marginBottom: 15 }} text={shownMessage.title} subtext={shownMessage.substitle} />
// 	// 				{/* <Button text='Regresar' style={{ marginTop: 15, minWidth: 200 }} onClick={()=>setShownError(null)} loading={false} /> */}
// 	// 			</div>
// 	// 		) : (loginTokens && !!user) ? (
// 	// 			<div className="contents full">
// 	// 				<Header
// 	// 					iconName='check-circle' 
// 	// 					iconStyle={{ color: '#1a9461', marginBottom: 10 }} 
// 	// 					text={`¡Hola, ${(user?.user.first_name || 'Usuario')}!`} 
// 	// 					subtext='Te redireccionaremos pronto a tu destino' 
// 	// 					subheaderStyle={{ marginTop: 5 }}
// 	// 					actions={(
// 	// 						<Button text='Continuar' size='tiny' style={{ padding: '2px 10px', minWidth: 100 }} basic color='green' as={Link} to={getContinueUrl(loginTokens)} />
// 	// 					)}
// 	// 				/>
// 	// 			</div>
// 	// 		) : params_valid ? <>
// 	// 			<div className="head">
// 	// 				<Header centered text={getHeaderText()} subtext={mode===LoginMode.FORGOT ? 'Olvidé mi contraseña' : null} />
// 	// 			</div>
// 	// 			{!!successMessage && (
// 	// 				<Message type="success" style={{ marginBottom: 15 }}>
// 	// 					{successMessage}
// 	// 				</Message>
// 	// 			)}
// 	// 			{mode===LoginMode.ACCOUNT_CREATED ? (
// 	// 				<div className="contents semi full">
// 	// 					<Header 
// 	// 						iconName='check-circle' 
// 	// 						iconStyle={{ fontSize: 70, marginBottom: 10 }} 
// 	// 						text='Cuenta creada' 
// 	// 						subtext={`¡Hemos creado tu cuenta AREMA!`} 
// 	// 						subheaderStyle={{ lineHeight: '16px' }} 
// 	// 					/>
// 	// 					<Message type='info' style={{ marginTop: 15, textAlign: 'center' }}>
// 	// 						Te hemos enviado un correo electrónico a <b>{CreateForm.data.email}</b> para que puedas confirmar tu cuenta
// 	// 					</Message>
// 	// 					<Button text='Iniciar sesión' color='black' style={{ marginTop: 15, minWidth: 200 }} onClick={returnMode} loading={false} />
// 	// 				</div>
// 	// 			) : (user_saved && !!user && show_saved) ? (
// 	// 				<div className="contents full semi">
// 	// 					{loginError ? <>
// 	// 						<Header iconName='exclamation-triangle' iconStyle={{ fontSize: 70, marginBottom: 15 }} text='Error' subtext={loginError} />
// 	// 						<Button text='Regresar' style={{ marginTop: 15, minWidth: 200 }} onClick={()=>setLoginError(null)} loading={false} />
// 	// 					</> : <>
// 	// 						<Header
// 	// 							iconName='user-circle' 
// 	// 							iconStyle={{ marginBottom: 10 }} 
// 	// 							text={`${user.user.first_name} ${user.user.last_name}`} 
// 	// 							subtext='Guardamos tu sesión'
// 	// 						/>
// 	// 						<Button text='Iniciar sesión' style={{ marginTop: 15, minWidth: 200 }} color='orange' onClick={refreshAuth} loading={sending} />
// 	// 						<Button text='Cerrar sesión' size='tiny' style={{ marginTop: 5, minWidth: 120 }} basic color='black' onClick={clearSaved} disabled={sending} />
// 	// 					</>}
// 	// 				</div>
// 	// 			)  : forgotSent ? (
// 	// 				<div className="contents semi full">
// 	// 					<Header 
// 	// 						iconName='envelope' 
// 	// 						iconStyle={{ fontSize: 70, marginBottom: 10 }} 
// 	// 						text='Enlace enviado' 
// 	// 						subtext={`Si existe una cuenta de AREMA con este correo electrónico, te enviamos un correo con instrucciones para restablecer tu contraseña`} 
// 	// 						subheaderStyle={{ lineHeight: '16px' }} 
// 	// 					/>
// 	// 					<Button text='Iniciar sesión' color='black' style={{ marginTop: 20, minWidth: 200 }} onClick={()=>{
// 	// 						setForgotSent(null);
// 	// 						setMode(LoginMode.LOGIN);
// 	// 					}} loading={false} />
// 	// 				</div>
// 	// 			) : mode===LoginMode.RECOVER && RecoverForm.data?.user===null ? (
// 	// 				<div className="contents semi full">
// 	// 					<Header 
// 	// 						loading
// 	// 						iconStyle={{ fontSize: 70, marginBottom: 10 }} 
// 	// 						text='Cargando enlace' 
// 	// 						subheaderStyle={{ lineHeight: '16px' }} 
// 	// 					/>
// 	// 				</div>
// 	// 			) : <>
// 	// 				{mode===LoginMode.LOGIN ? <>
// 	// 					<Input label='Correo electrónico' error={LoginForm.errors.email} value={LoginForm.data.email} onChange={LoginForm.onDataChange('email')} />
// 	// 					<Input label='Contraseña' style={{ marginBottom: 0 }} error={LoginForm.errors.password} value={LoginForm.data.password} onChange={LoginForm.onDataChange('password')} inputType='password' submitOnEnter onSubmit={submit} />
// 	// 					<Checkbox label={'Recordar mi sesión'} style={{ marginTop: 15 }} checked={LoginForm.data.remember} onChange={LoginForm.onDataChange('remember')} color='orange' />
// 	// 					{(is_pdv && LoginForm.data.remember) && (
// 	// 						<Input label='Código de acceso' comment='Usarás este código numérico para iniciar sesión rápidamente en el PDV' value={LoginForm.data.code} onChange={LoginForm.onDataChange('code')} labelStyle={{ textAlign: 'center' }} commentStyle={{ textAlign: 'center' }} style={{ marginTop: 20 }} inputStyle={{ textAlign: 'center', fontSize: 20 }} inputType='password' maxLength={4} />
// 	// 					)}
// 	// 					<Message type='error' list={LoginForm.prompts} style={{ marginTop: 15, marginBottom: -5 }} />
// 	// 					<Button color='orange' text='Iniciar sesión' fluid style={{ marginTop: 20 }} loading={sending} onClick={submit} />
// 	// 					{!is_pdv && <>
// 	// 						<Groupper.Divider type='line text' text={'Otras opciones'} />
// 	// 						<Button text='Crear cuenta' fluid iconName={'plus'} onClick={bindClose(setMode, LoginMode.CREATE)} />
// 	// 					</>}
// 	// 					<Groupper.Divider />
// 	// 					<div className="create">
// 	// 						¿No tienes una cuenta?
// 	// 						<Button text='Crear cuenta' size='tiny' basic color='black' onClick={bindClose(setMode, LoginMode.CREATE)} />
// 	// 					</div>
// 	// 					<div className="create">
// 	// 						<Button text='¿Olvidaste tu contraseña?' size='tiny' basic color='black' onClick={bindClose(setMode, LoginMode.FORGOT)} />
// 	// 					</div>
// 	// 				</> : mode===LoginMode.FORGOT ? <>
// 	// 					<div className="contents" style={{ minHeight: 260 }}>
// 	// 						<Message centered type='info' text={`Ingresa el correo electrónico de tu cuenta de AREMA`} style={{ marginBottom: 15 }} />
// 	// 						<Input label={'Correo electrónico'} error={LoginForm.errors.email} value={LoginForm.data.email} onChange={LoginForm.onDataChange('email')} />
// 	// 					</div>
// 	// 					<Message type='error' list={LoginForm.prompts} style={{ marginTop: 15, marginBottom: -5 }} />
// 	// 				</> : mode===LoginMode.CHANGE ? <>
// 	// 					<ChangePasswordMessage admin={ChangeForm.data.user.admin} email={ChangeForm.data.user.email} />
// 	// 					<Input label='Contraseña actual' error={ChangeForm.errors.current_password} value={ChangeForm.data.current_password} onChange={ChangeForm.onDataChange('current_password')} inputType='password' />
// 	// 					<Input label='Nueva contraseña' error={ChangeForm.errors.new_password} value={ChangeForm.data.new_password} onChange={ChangeForm.onDataChange('new_password')} inputType='password' />
// 	// 					<Input label='Repetir nueva contraseña' error={ChangeForm.errors.repeat_password} value={ChangeForm.data.repeat_password} onChange={ChangeForm.onDataChange('repeat_password')} inputType='password' />
// 	// 					<Message type='error' list={ChangeForm.prompts} style={{ marginTop: 15, marginBottom: -5 }} />
// 	// 				</> : mode===LoginMode.RECOVER ? (
// 	// 					RecoverForm.data.user ? <>
// 	// 						<ChangePasswordMessage admin={RecoverForm.data.user?.admin} email={RecoverForm.data.user?.email} />
// 	// 						<Input label='Nueva contraseña' error={RecoverForm.errors.new_password} value={RecoverForm.data.new_password} onChange={RecoverForm.onDataChange('new_password')} inputType='password' />
// 	// 						<Input label='Repetir nueva contraseña' error={RecoverForm.errors.repeat_password} value={RecoverForm.data.repeat_password} onChange={RecoverForm.onDataChange('repeat_password')} inputType='password' />
// 	// 						<Message type='error' list={RecoverForm.prompts} style={{ marginTop: 15, marginBottom: -5 }} />
// 	// 					</> : (
// 	// 						<div className="contents semi full">
// 	// 							<Header text={'Cargando datos'} loading />
// 	// 						</div>
// 	// 					)
// 	// 				) : mode===LoginMode.CREATE ? <>
// 	// 					<Input label='Nombre' error={CreateForm.errors.first_name} value={CreateForm.data.first_name} onChange={CreateForm.onDataChange('first_name')} />
// 	// 					<Input label='Apellidos' error={CreateForm.errors.last_name} value={CreateForm.data.last_name} onChange={CreateForm.onDataChange('last_name')} />
// 	// 					<Input label='Correo electrónico' error={CreateForm.errors.email} value={CreateForm.data.email} onChange={CreateForm.onDataChange('email')} inputType='email' />

// 	// 					<Groupper.Divider type='dashed' />
// 	// 					<Input label='Contraseña' error={CreateForm.errors.password} value={CreateForm.data.password} onChange={CreateForm.onDataChange('password')} inputType='password' />
// 	// 					<Input label='Repetir contraseña' error={CreateForm.errors.repeat_password || CreateForm.errors.password} value={CreateForm.data.repeat_password} inputType='password' onChange={CreateForm.onDataChange('repeat_password')} />
// 	// 					<Message type='error' list={CreateForm.prompts} style={{ marginTop: 15, marginBottom: -10 }} />
// 	// 				</> : null}
// 	// 			</>}
// 	// 		</> : confirm_token ? (
// 	// 			<div className="contents full">
// 	// 				<AccountConfirm token={confirm_token} />
// 	// 			</div>
// 	// 		) : (
// 	// 			<div className="contents full">
// 	// 				<Header iconName='exclamation-triangle' iconStyle={{ fontSize: 70, marginBottom: 15 }} text='Autenticación inválida' subtext='La autenticación de usuario fue mal configurada' />
// 	// 			</div>
// 	// 		)}
// 	// 		{CAPTCHA_ACTIVE && (
// 	// 			<ReCAPTCHA 
// 	// 				hl='es-419'
// 	// 				size='invisible'
// 	// 				sitekey={RECAPTCHA_KEY}
// 	// 				ref={recaptchaRef}
// 	// 			/>
// 	// 		)}
// 	// 	</Groupper>
// 	// </div>
// }

export default Login;